import { makeStyles, Theme } from "@material-ui/core";
import { green600, red500 } from "../../constants";

export interface AccessTypeResultCardStyleProps {
  variant?: "vigente" | "guardia" | "gray" | "employee" | "fuera-de-rango" | "paso-previo";
  checkboxPosition?: "left" | "right";
  disabled?: boolean;
  showAuthorizationDetails?: boolean;
}

export const useStyles = makeStyles<Theme, AccessTypeResultCardStyleProps>(() => ({
  container: (props) => {
    let border = `1px dashed ${green600}`;
    let backgroundColor = "#F0FDF4";
    let hoverBg = "#E6F7EA";
    let background: string | undefined = undefined;

    if (props?.variant === "gray") {
      border = "1px dashed #5B5D66";
      backgroundColor = "#FAFAFA";
      hoverBg = "#f0f0f0";
    } else if (props?.variant === "guardia") {
      border = "1px dashed #003350";
      backgroundColor = "#00A3FF22";
      hoverBg = "#00A3FF22";
    } else if (props?.variant === "employee") {
      border = "1px solid #2BE65E";
      background =
        "linear-gradient(90deg, rgba(12, 223, 212, 0.14) 0%, rgba(40, 232, 105, 0.14) 100%)";
      hoverBg =
        "linear-gradient(90deg, rgba(12, 223, 212, 0.14) 0%, rgba(40, 232, 105, 0.14) 100%)";
    } else if (props?.variant === "fuera-de-rango") {
      border = `1px dashed ${red500}`;
      backgroundColor = "#FEF2F2";
      hoverBg = "#FBE4E4";
    } else if (props?.variant === "paso-previo") {
      border = "1px dashed #FDBA74";
      backgroundColor = "#FFFBF5";
      hoverBg = "#FFF4E8";
    }

    return {
      display: "flex",
      flexDirection: "column" as const,
      boxSizing: "border-box" as const,
      cursor: props?.disabled ? "default" : "pointer",
      width: "100%",
      border,
      background: background || backgroundColor,
      borderRadius: "12px",
      transition: "background 0.2s ease-in-out",
      "&:hover": {
        background: props?.disabled ? background || backgroundColor : hoverBg,
      },
    };
  },
  headerRow: (props) => ({
    display: "flex",
    flexDirection: "row" as const,
    justifyContent: "space-between",
    alignItems: "center",
    padding: props?.checkboxPosition === "left" ? "16px 20px 16px 8px" : "16px 20px",
    gap: "16px",
    width: "100%",
    boxSizing: "border-box" as const,
    ...(props?.showAuthorizationDetails && {
      "@media (max-width: 900px)": {
        flexWrap: "wrap" as const,
      },
    }),
  }),
  titleGroup: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flex: "1",
    gap: "16px",
  },
  left: (props) => ({
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flex: "1",
    order: props?.checkboxPosition === "right" ? 1 : 0,
  }),
  personControl: {
    marginRight: 0,
    width: "100%",
    "& .MuiFormControlLabel-label": {
      width: "100%",
    },
  },
  companyContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  companyName: {
    margin: 0,
    fontWeight: 600,
    fontSize: "24px",
    color: "#003350",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  typePill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2px 10px",
    borderRadius: "100px",
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    width: "fit-content",
    backgroundColor: "#DBEAFE",
    color: "#1E40AF",
  },
  detailsSection: (props) => ({
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "24px",
    flex: "0 0 auto",
    justifyContent: "flex-end",
    order: props?.checkboxPosition === "right" ? 2 : 1,
    ...(props?.showAuthorizationDetails && {
      "@media (max-width: 900px)": {
        flexDirection: "column" as const,
        alignItems: "flex-start",
        gap: "12px",
        flex: "1 1 100%",
        paddingLeft: "8px",
      },
    }),
  }),
  detailItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  phoneDetailItem: {
    minWidth: "180px",
    flexShrink: 0,
  },
  managerDetailItem: {
    width: "320px",
    flexShrink: 0,
    minWidth: 0,
  },
  detailIcon: {
    fontSize: "32px",
    color: "#5B5D66",
  },
  detailText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  detailLabel: {
    fontSize: "18px",
    color: "#5B5D66",
    fontWeight: 500,
  },
  detailValue: {
    fontSize: "20px",
    color: "#1e293b",
    fontWeight: 500,
    lineHeight: 1.3,
    marginTop: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  leftIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "16px",
    "& svg": {
      fontSize: "36px",
      color: "#073642",
    },
  },
  checkboxWrapper: (props) => ({
    order: props?.checkboxPosition === "right" ? 3 : -1,
    display: "flex",
    alignItems: "center",
    marginLeft: props?.checkboxPosition === "right" ? "auto" : 0,
  }),
  checkbox: {
    "&.Mui-checked": {
      color: "#2BE65E",
    },
  },
  checkboxIcon: {
    fontSize: "32px",
  },
  checkedIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  tickBackground: {
    position: "absolute",
    backgroundColor: "white",
    width: "16px",
    height: "16px",
    zIndex: -1,
  },
  authDetailsRow: {
    display: "flex",
    flexDirection: "row" as const,
    // El estado (con sus badges), la vigencia y los días quedan centrados entre sí: cada bloque
    // tiene distinta cantidad de líneas y de otra forma cuelgan del borde de arriba.
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px 16px 20px",
    gap: "20px",
    width: "100%",
    boxSizing: "border-box" as const,
    borderTop: "1px dashed #CBD5E1",
    flexWrap: "wrap" as const,
    "@media (max-width: 900px)": {
      flexDirection: "column" as const,
      // Apilados, los bloques ocupan el ancho completo en vez de centrarse en la columna.
      alignItems: "stretch",
      gap: "16px",
    },
  },
  authStatusSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
    flex: "1 1 auto",
    minWidth: 0,
  },
  authStatusHeader: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: "6px",
    width: "100%",
  },
  authStatusIconValid: {
    fontSize: "24px",
    color: green600,
  },
  authStatusIconInvalid: {
    fontSize: "24px",
    color: red500,
  },
  authStatusTextValid: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: green600,
  },
  authStatusTextInvalid: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: red500,
  },
  authStatusHelper: {
    fontSize: "14px",
    color: "#5B5D66",
    lineHeight: 1.4,
    marginLeft: "30px",
    fontStyle: "italic" as const,
  },
  flagBadgesRow: {
    display: "flex",
    flexDirection: "row" as const,
    flexWrap: "wrap" as const,
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    // Los badges se van contra el borde derecho de la sección de estado, en vez de quedar pegados
    // al texto de "Autorización vigente".
    marginLeft: "auto",
  },
  authVigenciaSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    flex: "0 0 auto",
    borderLeft: "1px solid #E2E8F0",
    paddingLeft: "20px",
    "@media (max-width: 900px)": {
      borderLeft: "none",
      borderTop: "1px solid #E2E8F0",
      paddingLeft: 0,
      paddingTop: "12px",
      width: "100%",
    },
  },
  authVigenciaLabel: {
    fontSize: "14px",
    color: "#5B5D66",
    fontWeight: 500,
  },
  authVigenciaRow: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "flex-start",
    gap: "6px",
  },
  authVigenciaIcon: {
    fontSize: "20px",
    color: "#5B5D66",
    marginTop: "2px",
  },
  authVigenciaDate: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#003350",
  },
  authDaysSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
    flex: "0 0 auto",
    borderLeft: "1px solid #E2E8F0",
    paddingLeft: "20px",
    "@media (max-width: 900px)": {
      borderLeft: "none",
      borderTop: "1px solid #E2E8F0",
      paddingLeft: 0,
      paddingTop: "12px",
      width: "100%",
    },
  },
  authDaysLabel: {
    fontSize: "14px",
    color: "#5B5D66",
    fontWeight: 500,
  },
  authDaysPillsRow: {
    display: "flex",
    flexDirection: "row" as const,
    gap: "6px",
    flexWrap: "wrap" as const,
  },
  dayPillActive: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    backgroundColor: green600,
    color: "#FFFFFF",
    minWidth: "38px",
  },
  dayPillActiveInvalid: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 500,
    backgroundColor: "#c9cbcbff",
    color: "#828488ff",
    minWidth: "38px",
  },
  nextDayHint: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "4px",
    fontSize: "14px",
    color: "#5B5D66",
    marginTop: "2px",
  },
  nextDayHintIcon: {
    fontSize: "16px",
    color: "#5B5D66",
  },
}));
